import { ComponentRendererProps } from "@/store/sagas/types/deviceUnlock";
import React, { useState } from "react";
import InputRenderer from "./InputRenderer";
import SectionRenderer from "./SectionRenderer";
import CTARenderer from "./CTARenderer";
import ErrorBanner from "./ErrorBanner";

const DeviceStatusCheckRenderer: React.FC<ComponentRendererProps> = ({
  component,
  onSubmit,
  onNavigate,
  isRecaptchaReady = false,
  isRecaptchaEnabled = true,
  isSlowNetwork = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleValidationChange = React.useCallback((inputId: string, hasError: boolean) => {
    setInputErrors((prev) => {
      if (prev[inputId] === hasError) {
        return prev;
      }

      return {
        ...prev,
        [inputId]: hasError
      };
    });
  }, []);

  const hasInputError = Object.values(inputErrors).some(Boolean);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Submit the form
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const data: Record<string, FormDataEntryValue> = Object.fromEntries(formData.entries());

      if (onSubmit) {
        await Promise.resolve(onSubmit(data));
      }
    } catch {
      setSubmitError('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const sections = component?.details?.content?.section || [];
  const ctaSections = sections.filter((s) => !!s.cta);
  const contentSections = sections.filter((s) => !s.cta);

  return (
    <div className="w-full flex justify-center">
      <div className="w-[345px] sm:w-[800px] text-center">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col items-center">
            {submitError && (
              <ErrorBanner
                message={submitError}
                onClose={() => setSubmitError(null)}
              />
            )}

            {/* Content sections */}
            {contentSections.map((section) => (
              <div key={section.id} className="w-full">
                {section.title && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {section.title}
                  </h3>
                )}

                {section.description && (
                  <p className="text-gray-700 mb-4">{section.description}</p>
                )}

                {/* Input */}
                {section.input && (
                  <div className="w-full flex justify-center">
                    <InputRenderer
                      input={section.input}
                      onValidationChange={handleValidationChange}
                    />
                  </div>
                )}

                {/* Nested content */}
                {section.content?.section && (
                  <div className="mt-4 space-y-4">
                    {section.content.section.map((subsection) => (
                      <SectionRenderer
                        key={subsection.id}
                        section={subsection}
                        onSubmit={onSubmit}
                        onNavigate={onNavigate}
                        isSubmitting={isSubmitting}
                        hasInputError={hasInputError}
                        onInputValidationChange={handleValidationChange}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Submit button */}
            <div className="mt-6 w-full">
              {ctaSections.map((section) => (
                <div key={`${section.id}-cta`} className="flex justify-center">
                  <div className="w-full sm:max-w-[430px]">
                    {section.cta && (
                      <CTARenderer
                        cta={section.cta}
                        onNavigate={onNavigate}
                        isSubmitting={isSubmitting}
                        isRecaptchaReady={isRecaptchaReady}
                        isRecaptchaEnabled={isRecaptchaEnabled}
                        isSlowNetwork={isSlowNetwork}
                        hasInputError={hasInputError}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceStatusCheckRenderer;
